import { whyChooseFeatures, howItWorks } from '@/mocks/categories';

export default function ContentSections() {
  return (
    <>
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-200 text-secondary-800 text-xs font-semibold tracking-wide uppercase mb-4">About</span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-5">
                What Is Vigorbuy Spreadsheet?
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                The <strong>Vigorbuy Spreadsheet</strong> is a structured product discovery system designed to help shoppers find products faster and smarter. Instead of browsing scattered product listings across multiple websites, the Vigorbuy Spreadsheet consolidates thousands of organized product links into one centralized database.
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                Each product in the <strong>Vigorbuy Spreadsheet</strong> is categorized by type — shoes, clothing, accessories — making it easy to search, compare, and discover items based on your preferences. The system includes detailed product images, quality reference information, and direct links that connect you to the main shopping platform.
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                Whether you are a beginner looking for your first <strong>Vigorbuy Spreadsheet guide</strong> or an experienced shopper seeking the latest <strong>updated Vigorbuy Spreadsheet links</strong>, this platform provides everything you need in one convenient location.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                Explore Vigorbuy Spreadsheet
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20beautiful%20flat%20lay%20of%20curated%20lifestyle%20products%20including%20a%20pair%20of%20white%20sneakers%20a%20classic%20watch%20premium%20sunglasses%20and%20a%20leather%20wallet%20arranged%20on%20a%20soft%20cream%20textured%20surface%20with%20warm%20gentle%20morning%20sunlight%20and%20subtle%20shadows%20editorial%20product%20photography%20warm%20neutral%20aesthetic%20clean%20minimalist%20style&width=800&height=600&seq=about-products-v3&orientation=landscape"
                  alt="What is Vigorbuy Spreadsheet - Organized Product Discovery"
                  title="Vigorbuy Spreadsheet Product Organization"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white border-y border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wide uppercase mb-4">Why Choose</span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              Why Users Choose Vigorbuy Spreadsheet
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              Compare how the Vigorbuy Spreadsheet transforms the product discovery experience versus traditional browsing methods.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-background-200">
                  <th className="text-left py-4 px-4 text-sm font-semibold text-foreground-800">Feature</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-foreground-500">Normal Search</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-primary-600">Vigorbuy Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                {whyChooseFeatures.map((item) => (
                  <tr key={item.feature} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                    <td className="py-3.5 px-4 text-sm font-medium text-foreground-800">{item.feature}</td>
                    <td className="py-3.5 px-4 text-sm text-foreground-500">{item.normal}</td>
                    <td className="py-3.5 px-4 text-sm text-primary-700 font-medium">{item.spreadsheet}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-200 text-secondary-800 text-xs font-semibold tracking-wide uppercase mb-4">How It Works</span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              How Vigorbuy Spreadsheet Works
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              Getting started with the Vigorbuy Spreadsheet takes just four simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step) => (
              <div key={step.step} className="relative group">
                <div className="p-6 rounded-2xl bg-white border border-background-200 h-full transition-all duration-300 hover:border-primary-200">
                  <span className="text-4xl font-heading font-bold text-primary-200 mb-4 block">{step.step}</span>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}